import { useEffect } from 'react';

/**
 * Artesã DS — movimento nos títulos.
 *
 * Marca todo h1/h2/h3 com o atributo `data-h-in` quando ele entra na viewport,
 * disparando as animações definidas em index.css (entrada cinematográfica no H1,
 * traço em degradê no H2 e marca lateral no H3).
 *
 * Usa atributo (e não classe) porque o React reescreve `className` a cada
 * re-render, apagando classes adicionadas por fora do JSX.
 */
const SELECTOR = 'h1, h2, h3';
const MARK = 'data-h-in';

const HeadingMotion = () => {
  useEffect(() => {
    document.body.classList.add('ds-headings');

    let frame = 0;
    let stopped = false;

    const isVisible = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return false;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh * 0.96 && r.bottom > 0;
    };

    const scan = () => {
      frame = 0;
      if (stopped) return;
      document.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
        if (el.hasAttribute(MARK)) return;
        if (isVisible(el)) el.setAttribute(MARK, '');
      });
    };

    const schedule = () => {
      if (frame || stopped) return;
      frame = requestAnimationFrame(scan);
    };

    scan();

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });

    // rede de segurança para conteúdo que chega depois (fontes, imagens, dados)
    const interval = window.setInterval(schedule, 600);

    return () => {
      stopped = true;
      if (frame) cancelAnimationFrame(frame);
      window.clearInterval(interval);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      mo.disconnect();
      document.body.classList.remove('ds-headings');
    };
  }, []);

  return null;
};

export default HeadingMotion;
